import { EVENT_TYPE } from "../../AnalyticsTracking/events";
import { emitEvent } from "../../Shared/Assets/src/tagmanager";
import { getRequiredElement } from "../../Shared/Utilities/attributes";

type BreedSelectorStep = {
  alternatives: {
    score: number;
    title: string;
  }[];
  characteristic: string;
  goBackButtonLabel?: string;
  question: string;
  topicDescription: string;
  topicHeading: string;
};

type BreedSelectorData = {
  resultHeading?: string;
  resultPreamble?: string;
  steps: BreedSelectorStep[];
};

type StepState = "initial" | "results" | number;

const main = getRequiredElement<HTMLElement>(document, "main");

const goBackButton = getRequiredElement(document, "[data-go-back-button]");

const goBackButtonContent = getRequiredElement(document, "[data-go-back-button-content]");

const topicTitle = getRequiredElement(document, "[data-topic-title]");

const topicPreamble = getRequiredElement(document, "[data-topic-preamble]");

const startButton = getRequiredElement(document, "[data-start-button]");

const shareButton = getRequiredElement<HTMLButtonElement>(document, "[data-copy-button]");

const facebookShareButton = getRequiredElement<HTMLAnchorElement>(
  document,
  "[data-facebook-share-button]"
);

const progressCircle = getRequiredElement<SVGCircleElement>(
  document,
  "[data-progress-circle]"
);

const progressNumber = getRequiredElement<HTMLElement>(
  document,
  "[data-progress-number]"
);

const dashArray = parseFloat(progressCircle.getAttribute("stroke-dasharray") ?? "");

const questionTitle = getRequiredElement(document, "[data-question-title]");

const questionAnswers = getRequiredElement(document, "[data-question-answers]");

const answerTemplate = getRequiredElement(document, "[data-answer-template]");

const loadingIndicator = getRequiredElement<HTMLElement>(
  document,
  "[data-loading-indicator]"
);

const dataJson = getRequiredElement(document, "[data-breed-guide-data]").textContent;

const data = dataJson ? (JSON.parse(dataJson) as BreedSelectorData) : undefined;

if (!data?.steps) {
  throw "Breed guide json data missing";
}

const numSteps = data.steps.length;

if (numSteps < 1) {
  throw "Breed guide must have at least 1 step";
}

let currentStep: StepState = (main.dataset.step as StepState | undefined) ?? "initial";

const numStepVariants = 6;

let selectedStepIndices: number[] = [];

// Pre-populate selectedStepIndices if a query param is found
if (currentStep === "results") {
  const url = new URL(window.location.href);

  const param = url.searchParams.get("i");

  if (param) {
    selectedStepIndices = param.split(",").map(val => parseInt(val));
  }
}

const updateStep = () => {
  if (currentStep !== "initial") {
    let goBackButtonLabel: string | undefined;

    if (currentStep === "results") {
      main.dataset.step = "results";
      main.dataset.stepColor = "results";

      topicTitle.innerHTML = data.resultHeading ?? "";
      topicPreamble.innerHTML = data.resultPreamble ?? "";

      goBackButtonLabel = data.steps[numSteps - 1].goBackButtonLabel;

      // Update progress indicator circle
      progressCircle.setAttribute("stroke-dashoffset", "0");

      // Update share button url
      const url = new URL(window.location.href);

      url.search = "";
      url.searchParams.set("i", selectedStepIndices.join(","));

      shareButton.dataset.copyText = url.toString();

      // Update facebook share button url, requires some additional encoding shenanigans
      url.search = decodeURIComponent(url.search);

      facebookShareButton.href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        url.toString()
      )}`;

      emitEvent(EVENT_TYPE.breedGuideResults, {}, false);
    } else {
      const stepData = data.steps[currentStep];

      main.dataset.step = `${currentStep}`;

      // Modulo the step color to support an arbitrary number of steps
      main.dataset.stepColor = `${currentStep % numStepVariants}`;

      topicTitle.innerHTML = stepData.topicHeading;
      topicPreamble.innerHTML = stepData.topicDescription;
      questionTitle.innerHTML = stepData.question;
      questionAnswers.innerHTML = "";

      // Update progress indicator circle
      progressCircle.setAttribute(
        "stroke-dashoffset",
        `${dashArray - (dashArray * (currentStep + 1)) / (numSteps + 2)}`
      );

      progressNumber.innerHTML = `${currentStep + 1}`;

      // Update question alternatives/answers
      for (let index = 0; index < stepData.alternatives.length; ++index) {
        const alternative = stepData.alternatives[index];

        const button = answerTemplate.cloneNode() as HTMLElement;
        const li = document.createElement("li");

        button.innerHTML = alternative.title;
        button.title = alternative.title;

        if (selectedStepIndices[currentStep] === index) {
          button.ariaCurrent = "true";
        }

        li.appendChild(button);
        questionAnswers.appendChild(li);

        button.addEventListener("click", async () => {
          if (typeof currentStep === "number") {
            selectedStepIndices[currentStep] = index;

            if (currentStep < numSteps - 1) {
              currentStep += 1;
            } else {
              currentStep = "results";
            }

            updateStep();

            if (currentStep === "results") {
              // Setup htmx request to fetch results list
              button.setAttribute("hx-indicator", "[data-loading-indicator]");

              // Setting inert mainly for a11y here
              loadingIndicator.inert = false;

              await htmx.ajax("GET", window.location.pathname, {
                select: "[data-results-list]",
                source: button,
                swap: "outerHTML",
                target: "[data-results-list]",
                values: {
                  i: selectedStepIndices.join(",")
                }
              });

              document
                .querySelectorAll<HTMLAnchorElement>(
                  "[data-results-list] [data-breed-card-link]"
                )
                .forEach(link => {
                  link.addEventListener("click", () => {
                    const data: Record<string, unknown> = {
                      breedName: link.dataset.breedName,
                      matchPercent: link.dataset.matchPercent
                    };

                    emitEvent(EVENT_TYPE.breedGuideResultSelected, data, true);
                  });
                });

              loadingIndicator.inert = true;
            }
          }
        });
      }

      if (currentStep > 0) {
        goBackButtonLabel = data.steps[currentStep - 1].goBackButtonLabel;
      }
    }

    if (goBackButtonLabel) {
      goBackButtonContent.innerHTML = goBackButtonLabel;
    }
  }
};

startButton.addEventListener("click", () => {
  currentStep = 0;

  updateStep();
});

goBackButton.addEventListener("click", () => {
  if (typeof currentStep === "number") {
    currentStep -= 1;
  } else {
    // Go from 'results' to last step
    currentStep = numSteps - 1;
  }

  updateStep();
});

updateStep();
